import _ from 'lodash'
import KeyValuePair from '@/modules/core/support/KeyValuePair'

/**
 * Campos SEO modificados por el usuario
 */
export type TouchedFields = Map<string, string[]>

/**
 * Interfaz para los componentes encargados de auto-completar la información
 * del SEO en base a información externa
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol>
 */
export interface SEOCompletor {

    /**
     * Modifica la entidad de metadatos SEO para completarla con información
     * externa
     *
     * @param metadata
     * @param currentLocale
     */
    complete (metadata: Metadata, currentLocale: string): Metadata

    /**
     * Genera un mapa de los campos modificados por el usuario
     *
     * @param metadata
     * @param touchedFields
     */
    initFields (metadata: Metadata, touchedFields: TouchedFields): Map<string, string[]>
}

/**
 * Define la interfaz que las entidades deberán implementar si desean ofrecer
 * información sobre su posicionamiento orgánico
 */
export interface SEOPositionable {
    seo: Metadata
}

/**
 * Metadatos SEO
 *
 * Define los metadatos asociados a una entidad
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class Metadata {
    public id = 0
    public readonly tempId = _.uniqueId('seo_translation_')

    public translations: MetadataTranslation[] = []

    /**
     * Indica si el objeto esta vacío
     *
     * @returns {boolean}
     */
    get isEmpty (): boolean {
        return !this.translations.length
    }

    /**
     * Comprueba si existe una traducción
     *
     * @param {string} locale
     * @returns {boolean}
     */
    public hasTranslation (locale: string): boolean {
        return this.translations.findIndex(t => t.locale === locale) >= 0
    }

    /**
     * Añade una nueva traducción a la lista
     *
     * @param {MetadataTranslation} translation
     * @returns {this}
     */
    public addTranslation (translation: MetadataTranslation): this {
        if (!translation.locale) {
            throw new Error('El localismo de la traducción no puede estar vacío')
        }

        // Si la traducción ya existe se sobre-escribe la actual por la nueva insertada
        const index = this.translations.findIndex(t => t.locale === translation.locale)
        if (index >= 0) {
            this.translations.splice(index, 1, translation)

            return this
        }

        this.translations.push(translation)

        return this
    }

    public translate (locale: string) {
        return this.translations.find(t => t.locale === locale)
    }

    /**
     * Vuelca la información de un objeto JSON plano en una nueva entidad con
     * los metadatos del SEO.
     *
     * @param data
     */
    public static deserialize (data: {[key: string]: any}): Metadata {
        const metadata = new Metadata()
        const translations: object[] = data.translations

        if (!translations || !Array.isArray(translations)) {
            return metadata
        }

        metadata.id = data.id
        metadata.translations = translations.map(t => MetadataTranslation.deserialize(t))

        return metadata
    }

    public serialize (): {[key: string]: any} {
        return {
            id: this.id,
            translations: this.translations.map(t => t.serialize()),
        }
    }
}

/**
 * Traducción
 */
export class MetadataTranslation {
    public id = 0
    public title = ''
    public description = ''
    public slug = ''
    public keywords: string[] = []
    public opengraph = new OpenGraph()
    public twitter = new TwitterCards()

    constructor (public locale = '') {}

    get isEmpty (): boolean {
        return !this.id && !this.title && !this.description && !this.slug && !this.keywords.length
    }

    public static deserialize (data: {[key: string]: any}): MetadataTranslation {
        const translation = new MetadataTranslation()
        translation.id = data.id
        translation.locale = data.locale
        translation.title = data.title
        translation.slug = data.slug
        translation.description = data.description
        translation.keywords = data.keywords
        translation.opengraph = OpenGraph.deserialize(data.opengraph || {})
        translation.twitter = TwitterCards.deserialize(data.twitter || {})

        return translation
    }

    public serialize () {
        return {
            id: this.id,
            locale: this.locale,
            title: this.title,
            slug: this.slug,
            description: this.description,
            keywords: this.keywords,
            twitter: this.twitter.serialize(),
            opengraph: this.opengraph.serialize(),
        }
    }
}

/**
 * Articulo OpenGraph
 */
export class OpenGraphArticle {
    public publishedTime = ''
    public modifiedTime = ''
    public expirationTime = ''
    public author = new OpenGraphProfile()
    public section = ''
    public tags: string[] = []

    public static deserialize (data: {[key: string]: any}): OpenGraphArticle {
        const article = new OpenGraphArticle()
        article.publishedTime = data.published_time
        article.modifiedTime = data.modified_time
        article.expirationTime = data.expiration_time
        article.author = OpenGraphProfile.deserialize(data.author || {})
        article.section = data.section
        article.tags = data.tag

        return article
    }

    public serialize (): {[key: string]: any} {
        return {
            published_time: this.publishedTime,
            modified_time: this.modifiedTime,
            expiration_time: this.modifiedTime,
            author: this.author.serialize(),
            section: this.section,
            tag: this.tags,
        }
    }

}

/**
 * Grupos de edad soportados por la referencia de OpenGraph de Facebook
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export enum AgeGroup {
    Kids = 'kids',
    Adults = 'adults',
    AllAges = 'all ages',
    Newborn = 'newborn',
    Infant = 'infant',
    Teen = 'teen',
}

/**
 * Estado de disponibilidad de un producto
 */
export enum ProductAvailability {
    InStock = 'instock',
    Preorder = 'preorder',
    OutOfStock = 'out of stock',
    Pending = 'pending',
    AvailableForOrder = 'available for order',
}

/**
 * Condición de un producto
 */
export enum ProductCondition {
    New = 'new',
    Used = 'used',
    Refurbished = 'refurbished',
}

/**
 * Producto de tienda
 *
 * Descripción de Facebook:
 * Este tipo de objeto representa un producto. Esto incluye productos virtuales y
 * físicos, pero típicamente representa elementos que están disponibles en una tienda en línea.
 *
 * @see https://developers.facebook.com/docs/reference/opengraph/object-type/product/
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class OpenGraphProduct {
    public ageGroup = AgeGroup.Adults
    public availability = ProductAvailability.Pending
    public brand = ''
    public category = ''
    public color = ''
    public condition = ProductCondition.New
    public ean = ''
    public expirationTime = ''
    public isbn = ''
    public material = ''
    public manufacturersPartNumber = ''
    // public originalPrice: Array<Currency>
    public pattern = ''
    public pluralTitle = ''
    // public pretaxPrice: Array<Currency>
    // public price: Array<Currency>
    public purchaseLimit = 0
    // public retailer: ProfileID
    public retailerCategory = ''
    public retailerPartNumber = ''
    public retailerTitle = ''
    // public salePrice: Currency
    // public salePricesRate: DateTimeRange
    // public shippingCost: Array<Currency>
    public shippingWeight = 0
    public size = ''
    public targetGender = OpenGraphGender.Unisex
    public upc = ''
    public weight = 0
}

/**
 * Imagen de OpenGraph
 */
export class OpenGraphImage {
    public source = ''
    public url = ''
    public secureUrl = ''
    public type: MimeType
    public width = 0
    public height = 0
    public alt = ''
}

export class OpenGraphAudio {
    public source = ''
    public type: MimeType
    public secureUrl = ''
}

export class OpenGraphVideo {
    public source = ''
    public secureUrl = ''
    public type = MimeType
    public width = 0
    public height = 0
}

export enum OpenGraphGender {
    Male = 'male',
    Female = 'female',
    Other = 'other',
    Unisex = 'unisex',
    Unspecified = 'unspecified',
}

export class OpenGraphBook {
    public author: OpenGraphProfile
    public isbn = ''
    public releaseDate = ''
    public tag: string[] = []
}

/**
 * Perfil de usuario
 *
 * Representa la información de un usuario en una plataforma
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class OpenGraphProfile {
    public firstName = ''
    public lastName = ''
    public username = ''
    public gender = OpenGraphGender.Unspecified

    /**
     * Vuelva el contenido JSON en una entidad de perfil
     *
     * @param {{[p: string]: any}} data
     * @returns {OpenGraphProfile}
     */
    public static deserialize (data: {[key: string]: any}): OpenGraphProfile {
        const profile = new OpenGraphProfile()
        profile.firstName = data.first_name
        profile.lastName = data.last_name
        profile.username = data.username
        profile.gender = data.gender

        return profile
    }

    /**
     * Convierte una entidad perfil de OpenGraph en JSON
     *
     * @returns {{[p: string]: any}}
     */
    public serialize (): {[key: string]: any} {
        return {
            first_name: this.firstName,
            last_name: this.lastName,
            username: this.username,
            gender: this.gender.toString(),
        }
    }
}

/**
 * Tipos de objetos OpenGraph según el estándar del protocolo
 */
export enum OpenGraphTypes {
    // Tipos de música
    Song = 'music.song',
    Album = 'music.album',
    Playlist = 'music.playlist',
    RadioStation = 'music.radio_station',

    // Tipos de video
    Movie = 'video.movie',
    Episode = 'video.music',
    TVShow = 'video.tv_show',
    VideoOther = 'video.other',

    // Otros tipos
    Article = 'article',
    Book = 'book',
    Profile = 'profile',
    Website = 'website',

    // Tipos de Facebook
    ProductItem = 'product.item',
}

export class SocialMeta {
    protected properties = new Map<string, any>()

    /**
     * Indica si el objeto OpenGraph esta vacío
     *
     * @returns {boolean}
     */
    get isEmpty (): boolean {
        return this.properties.size === 0
    }

    /**
     * Obtiene el valor de una propiedad
     *
     * @param key Nombre de la propiedad
     * @param _default
     */
    public getProperty (key: string, _default: any = null) {
        return this.properties.get(key) || _default
    }

    /**
     * Añade una nueva propiedad
     *
     * Si la propiedad ya existe, se sustituye por un array con todos los
     * posibles valores de esa propiedad.
     *
     * @param key
     * @param value
     */
    public addProperty (key: string, value: any): this {
        return this.insert(key, value, true)
    }

    /**
     * Comprueba si una propiedad ya existe
     *
     * @param key
     */
    public hasProperty (key: string): boolean {
        return this.properties.has(key)
    }

    /**
     *
     * Inserta una propiedad
     *
     * @param key
     * @param value
     * @param replace
     */
    protected insert (key: string, value: any, replace = false): this {
        const property = this.properties.get(key)

        // Si la propiedad no existe o se ha marcado para reemplazar, se escribe
        // directamente
        if (replace || !property) {
            this.properties.set(key, value)

            return this
        }

         // Si la propiedad es un array de valores, se incluye el nuevo
        if (Array.isArray(property)) {
            property.push(value)
        } else {
            // Si la propiedad ya existe y no es un array se sustituye la propiedad
            // por un nuevo Array con todos los valores
            this.properties.set(key, [property, value])
        }

        this.properties.set(key, property)

        return this
    }
}


/**
 * Representa la información Opengraph relacionada con una entidad
 *
 * @see http://opengraphprotocol.org/
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class OpenGraph extends SocialMeta {

    private static readonly conversionMap: KeyValuePair = {
        image: { type: OpenGraphImage, setter: 'addImage' },
        article: { type: OpenGraphArticle, setter: 'article' },
    }

    /**
     * Inserta una propiedad
     *
     * @param opengraph
     * @param type
     * @param value
     */
    private static insertProperty (opengraph: OpenGraph, type: string, value: any) {
        const conversion = this.conversionMap[type] || { method: 'addProperty' }

        // Convierte el valor, el tipo debe implementar el método deserialize()
        const deserializedValue = conversion.type
            ? conversion.type.deserialize(value)
            : value

        // Si la conversión declara un método, se invoca a este
        if (_.has(conversion, 'method')) {
            const args = [deserializedValue]
            if (conversion.method === 'addProperty') {
                args.unshift(type)
            }

            _.invoke(opengraph, conversion.method, ...args)
            // opengraph[conversion.method](...args)

            return this
        }

        // Si se declara un setter se llama como una propiedad
        if (_.has(conversion, 'setter')) {
            _.set(opengraph, conversion.setter, deserializedValue)

            return this
        }

        return this
    }

    constructor (public prefix = 'og') {
        super()
    }

    /**
     * Permite registra la información de un artículo
     */
    set article (article: OpenGraphArticle) {
        this.insert('article', article, true)
    }

    /**
     * Permite acceder al articulo de OpenGraph almacenado, si no existe
     * se retornarná un articulo vacio
     */
    get article (): OpenGraphArticle {
        return this.getProperty('article', new OpenGraphArticle())
    }

    // set profile (profile: OpenGraphProfile) {}

    /**
     * Título del objeto
     *
     * Devuelve el contenido de la propiedad og:title
     */
    get title (): string {
        return this.getProperty('title', '')
    }

    /**
     * Titulo del objeto
     *
     * Establece la propiedad og:title del objeto
     */
    set title (title: string) {
        this.insert('title', title, true)
    }

    get url (): string {
        return this.getProperty('url', '')
    }

    set url (url: string) {
        this.insert('url', url, true)
    }

    /**
     * Establece el tipo OpenGraph del objeto
     */
    set type (type: OpenGraphTypes) {
        if (this.type === type) {
            return
        }

        this.insert('type', type, true)
    }

    /**
     * Retorna el tipo OpenGraph del objeto
     */
    get type (): OpenGraphTypes {
        return this.getProperty('type', '')
    }


    /**
     * Añade una imagen al objeto OpenGraph
     *
     * @param image
     */
    public addImage (image: OpenGraphImage): this {
        return this.insert('image', image)
    }

    /**
     * Añade un audio al objeto OpenGraph
     *
     * @param audio
     */
    public addAudio (audio: OpenGraphAudio): this {
        return this.insert('audio', audio)
    }


    /**
     * Vuelva la información Opengraph del SEO en JSON en una entidad Opengraph
     *
     * @param data
     */
    public static deserialize (data: KeyValuePair) {
        const opengraph = new OpenGraph(data.prefix || 'og')

        // Recorre la lista de atributos y los inserta en el objeto OpenGraph
        _.forOwn(data.properties || {}, (value: any, type: string) => {
            if (Array.isArray(value)) {
                this.insertProperty(opengraph, type, value)
            }

            this.insertProperty(opengraph, type, value)
        })

        return opengraph
    }

    /**
     * Serializa el contenido de la entidad
     *
     * @returns {{prefix: string | undefined; properties: {}}}
     */
    public serialize () {
        const data: { prefix: string, properties: KeyValuePair } = {
            prefix: this.prefix,
            properties: {},
        }

        this.properties.forEach((value: { serialize?: () => any}, key) => {
            if (_.isObject(value) && _.has(value, 'serialize') && _.isFunction(value.serialize)) {
                data.properties[key] = value.serialize()

                return
            }

            data.properties[key] = value
        })

        return data
    }
}

export enum TwitterCardTypes {
    Summary = 'summary',
    SummaryLargeImage = 'summary_large_image',
    App = 'app',
    Player = 'player',
    None = 'none',
}

export class TwitterCardImage {
    public source = ''
    public alt = ''
}


/**
 * Tarjeta de Twitter
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export class TwitterCards extends SocialMeta {
    constructor (public prefix = 'twitter') {
        super()
    }

    /**
     * Permite establecer el tipo de Tweet que se mostrará
     * @param {TwitterCardTypes} type
     */
    set type (type: TwitterCardTypes) {
        this.insert('card', type, true)
    }

    /**
     * Retorna el tipo de Tweet
     *
     * @returns {TwitterCardTypes}
     */
    get type (): TwitterCardTypes {
        return this.getProperty('card', TwitterCardTypes.None)
    }

    /**
     * Establece la cuenta de Twitter que será mostrada como lugar donde se
     * publicó el contenido
     *
     * @param {string} site
     */
    set site (site: string) {
        this.insert('site', site, true)
    }

    /**
     * Retorna la cuenta de Twitter publicó el contenido
     *
     * @returns {string}
     */
    get site (): string {
        return this.getProperty('site', '')
    }

    /**
     * Retorna la descripción del contenido
     *
     * @returns {string}
     */
    get description (): string {
        return this.getProperty('description', '')
    }

    /**
     * Permite establecer la descripción del contenido
     *
     * @param {string} description
     */
    set description (description: string) {
        this.insert('description', description, true)
    }

    set creator (creator: string) {
        this.insert('creator', creator, true)
    }

    get creator (): string {
        return this.getProperty('creator', '')
    }

    get title (): string {
        return this.getProperty('title', '')
    }

    set title (title: string) {
        this.addProperty('title', title)
    }

    set image (image: string) {
        this.addProperty('image', image)
    }

    get image (): string {
        return this.getProperty('image', '')
    }

    set imageAlt (alt: string) {
        this.addProperty('image:alt', alt)
    }

    get imageAlt (): string {
        return this.getProperty('image:alt', '')
    }

    /**
     * Añade una imagen a la tarjeta de Twitter
     * @param image
     */
    public addImage (image: TwitterCardImage): this {
        return this.insert('image', image, false)
    }

    /**
     * Vuelva el contenido JSON en una entidad con las tarjetas de Twitter
     *
     * @param {[p: string]: any} data
     * @returns {TwitterCards}
     */
    public static deserialize (data: {[key: string]: any}): TwitterCards {
        const twitter = new TwitterCards()

        _.forOwn(data.properties, (value, key) => twitter.addProperty(key, value))

        return twitter
    }

    /**
     * Convierte la entidad en un objeto serializable para JSON
     */
    public serialize () {
        return {
            prefix: this.prefix,
            properties: this.serializeProperties(),
        }
    }

    private serializeProperties () {
        const properties: KeyValuePair = {}
        this.properties.forEach((value, key) => properties[key] = value)

        return properties
    }
}

export enum MimeTypes {
    a      = 'application/octet-stream',
    ai     = 'application/postscript',
    aif    = 'audio/x-aiff',
    aifc   = 'audio/x-aiff',
    aiff   = 'audio/x-aiff',
    au     = 'audio/basic',
    avi    = 'video/x-msvideo',
    bat    = 'text/plain',
    bin    = 'application/octet-stream',
    bmp    = 'image/x-ms-bmp',
    c      = 'text/plain',
    cdf    = 'application/x-cdf',
    csh    = 'application/x-csh',
    css    = 'text/css',
    dll    = 'application/octet-stream',
    doc    = 'application/msword',
    dot    = 'application/msword',
    dvi    = 'application/x-dvi',
    eml    = 'message/rfc822',
    eps    = 'application/postscript',
    etx    = 'text/x-setext',
    exe    = 'application/octet-stream',
    gif    = 'image/gif',
    gtar   = 'application/x-gtar',
    h      = 'text/plain',
    hdf    = 'application/x-hdf',
    htm    = 'text/html',
    html   = 'text/html',
    jpe    = 'image/jpeg',
    jpeg   = 'image/jpeg',
    jpg    = 'image/jpeg',
    js     = 'application/x-javascript',
    ksh    = 'text/plain',
    latex  = 'application/x-latex',
    m1v    = 'video/mpeg',
    man    = 'application/x-troff-man',
    me     = 'application/x-troff-me',
    mht    = 'message/rfc822',
    mhtml  = 'message/rfc822',
    mif    = 'application/x-mif',
    mov    = 'video/quicktime',
    movie  = 'video/x-sgi-movie',
    mp2    = 'audio/mpeg',
    mp3    = 'audio/mpeg',
    mp4    = 'video/mp4',
    mpa    = 'video/mpeg',
    mpe    = 'video/mpeg',
    mpeg   = 'video/mpeg',
    mpg    = 'video/mpeg',
    ms     = 'application/x-troff-ms',
    nc     = 'application/x-netcdf',
    nws    = 'message/rfc822',
    o      = 'application/octet-stream',
    obj    = 'application/octet-stream',
    oda    = 'application/oda',
    pbm    = 'image/x-portable-bitmap',
    pdf    = 'application/pdf',
    pfx    = 'application/x-pkcs12',
    pgm    = 'image/x-portable-graymap',
    png    = 'image/png',
    pnm    = 'image/x-portable-anymap',
    pot    = 'application/vnd.ms-powerpoint',
    ppa    = 'application/vnd.ms-powerpoint',
    ppm    = 'image/x-portable-pixmap',
    pps    = 'application/vnd.ms-powerpoint',
    ppt    = 'application/vnd.ms-powerpoint',
    pptx    = 'application/vnd.ms-powerpoint',
    ps     = 'application/postscript',
    pwz    = 'application/vnd.ms-powerpoint',
    py     = 'text/x-python',
    pyc    = 'application/x-python-code',
    pyo    = 'application/x-python-code',
    qt     = 'video/quicktime',
    ra     = 'audio/x-pn-realaudio',
    ram    = 'application/x-pn-realaudio',
    ras    = 'image/x-cmu-raster',
    rdf    = 'application/xml',
    rgb    = 'image/x-rgb',
    roff   = 'application/x-troff',
    rtx    = 'text/richtext',
    sgm    = 'text/x-sgml',
    sgml   = 'text/x-sgml',
    sh     = 'application/x-sh',
    shar   = 'application/x-shar',
    snd    = 'audio/basic',
    so     = 'application/octet-stream',
    src    = 'application/x-wais-source',
    swf    = 'application/x-shockwave-flash',
    t      = 'application/x-troff',
    tar    = 'application/x-tar',
    tcl    = 'application/x-tcl',
    tex    = 'application/x-tex',
    texi   = 'application/x-texinfo',
    texinfo= 'application/x-texinfo',
    tif    = 'image/tiff',
    tiff   = 'image/tiff',
    tr     = 'application/x-troff',
    tsv    = 'text/tab-separated-values',
    txt    = 'text/plain',
    ustar  = 'application/x-ustar',
    vcf    = 'text/x-vcard',
    wav    = 'audio/x-wav',
    wiz    = 'application/msword',
    wsdl   = 'application/xml',
    xbm    = 'image/x-xbitmap',
    xlb    = 'application/vnd.ms-excel',
    xls    = 'application/vnd.ms-excel',
    xlsx    = 'application/vnd.ms-excel',
    xml    = 'text/xml',
    xpdl   = 'application/xml',
    xpm    = 'image/x-xpixmap',
    xsl    = 'application/xml',
    xwd    = 'image/x-xwindowdump',
    zip    = 'application/zi',
  }


/**
 * Reporte del analisis SEO
 */
export class AnalysisReport {
    public constructor (public rules = new RuleSet()) {}

    public static deserialize (data: KeyValuePair) {
        return new this(RuleSet.deserialize(data.rules))
    }
}

export default class BaseRule {
    public key = ''
    public name = ''
    public description = ''
    public type = ''
}

/**
 * Estados de la regla
 */
export enum RuleStatus {
    Pass = 'pass',
    Fail = 'fail',
    Warning = 'warning',
}

/**
 * Conjunto de reglas del analisis SEO
 */
export class RuleSet extends BaseRule {
    constructor (public rules: Array<Rule | RuleSet>= []) {
        super()
    }

    /**
     * Busca una regla o grupo de reglas por clave
     *
     * @param key {string]
     */
    public get (key: string, _default?: any): Rule | RuleSet | any {
        return this.rules.find((r) => r.key === key)
    }

    /**
     * Convierte un objeto plano con la información en una entidad
     *
     * @param data { KeyValuePair }
     */
    public static deserialize (data: KeyValuePair) {
        const rules = data.rules.map((r: KeyValuePair) => {
            if (r.type === 'singleRule') {
                return Rule.deserialize(r)
            }

            if (r.type === 'ruleSet') {
                const ruleSet = new RuleSet()
                ruleSet.key = r.key
                ruleSet.name = r.name
                ruleSet.description = r.description

                return ruleSet
            }

            throw new TypeError('La regla deserialaza no contiene un tipo válido')
        })

        return new this(rules)
    }
}

export class Rule extends BaseRule {
    public status: RuleStatus
    private attributes: KeyValuePair = {}

    public getAttribute (key: string, _default?: any): any {
        return this.attributes[key] || _default
    }

    public static deserialize (data: KeyValuePair) {
        const rule = new this()
        rule.type = data.type
        rule.description = data.description
        rule.key = data.key
        rule.name = data.name
        rule.status = data.status
        rule.attributes = data.attributes

        return rule
    }
}
